import {
    ICO2Emission,
    ICO2MeterReading,
    IEmissionsDataForChart,
    IMeterReadingsElement,
    MeterType,
} from 'co2Meters/types/co2Meters';
import { TFunction } from 'react-i18next';
import { DateTime } from 'luxon';

export const momentKeyFormat = 'MMMMYYYY';
export const luxonKeyFormat = 'MMMMyyyy';
const monthsRequired = 11;

type CreateEmissionsDataForChartType = (
    emissions: ICO2Emission[] | null,
    meterReadings?: ICO2MeterReading[] | null,
    agreementStartDate?: string | null,
) => IEmissionsDataForChart[];

export const createEmissionsDataForChart: CreateEmissionsDataForChartType = (
    emissions,
    meterReadings,
    agreementStartDate,
) => {
    // Return empty array if no emissions data
    if (!emissions) return [];

    // Calculate how many months to show in the chart
    const months: string[] = [];
    const monthRange = Math.ceil(DateTime.now().diff(DateTime.fromSQL(agreementStartDate || ''), 'month')?.months);
    const monthInChart = monthRange > monthsRequired ? monthRange : monthsRequired;

    // Create array of month identifiers (e.g. "January2024", "February2024")
    for (let i = monthInChart; i >= 0; i--) {
        months.push(DateTime.now().minus({ month: i }).toFormat(luxonKeyFormat));
    }

    // Convert emissions array to object for quick lookup by month
    const emissionsDatesDictionary = emissions.reduce<Record<string, number>>((acc, { period, value }) => {
        const dateKey = DateTime.fromSQL(period).toFormat(luxonKeyFormat);
        acc[dateKey] = value > 0 ? value : 0;
        return acc;
    }, {});

    // Convert meter readings to nested object grouped by month and meter type
    const meterReadingsDatesDictionary = (meterReadings || []).reduce<
        Record<string, Record<MeterType, IMeterReadingsElement>>
    >((acc, { period, value, meterType, unit }) => {
        const dateKey = DateTime.fromSQL(period).toFormat(luxonKeyFormat);
        acc[dateKey] = { ...acc[dateKey], [meterType]: { value, unit } };
        return acc;
    }, {});

    // Create final chart data by combining all the data for each month
    return months.map((month, index) => ({
        name: month.slice(0, 3),
        co2: emissionsDatesDictionary[month] || 0,
        id: month,
        meterReadings: meterReadingsDatesDictionary[month] || null,
        index,
    }));
};

export const findLastExistMonthIdWithEmission = (emissions: ICO2Emission[]): string | undefined => {
    // Find the most recent month that has emission data > 0
    for (let i = 0; i <= emissions.length; i++) {
        if (emissions[emissions.length - i]?.value > 0) {
            return DateTime.fromSQL(emissions[emissions.length - i]?.period).toFormat(luxonKeyFormat);
        }
    }
};

export const prepareCO2ChartData = (
    chartEmissions: IEmissionsDataForChart[],
    agreementStartDate?: string | null,
): Partial<IEmissionsDataForChart>[] => {
    if (!agreementStartDate) return [];

    // Find the month before agreement started
    const startAgreementMonthId = DateTime.fromSQL(agreementStartDate).minus({ month: 1 }).toFormat(luxonKeyFormat);
    const startChartIndex = chartEmissions.find(({ id }) => id === startAgreementMonthId)?.index || 0;

    // Only show CO2 data for months after the agreement started
    return chartEmissions.map(({ id, co2, index, name }) => {
        const monthChartData: Partial<IEmissionsDataForChart> = { id, name };
        if (index >= startChartIndex) monthChartData.co2 = co2;
        return monthChartData;
    });
};

export const createMonthWithYearTitle = (t: TFunction, monthId?: string | null): string => {
    if (!monthId || typeof monthId !== 'string') return '';

    // Extract month name and year from monthId and translate the month
    const translatedMonth = t(`months.${monthId.slice(0, -4).toLowerCase()}`);
    return `${translatedMonth} ${monthId.slice(-4)}`;
};
